import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { hasHasilPage } from '@/types/sports';
import mabarService, {
  EventDetailResponse,
  GameListItem,
  StandingItem,
} from '@/services/mabarService';
import communityService, { MabarParticipant } from '@/services/communityService';
import invasionService, { InvasionSession } from '@/services/invasionService';
import StandingCard from '@/components/community/StandingCard';
import InvasionPlayerLeaderboard, {
  InvasionPlayerLeaderboardItem,
} from '@/components/community/InvasionPlayerLeaderboard';
import PadelStandingsBoard from '@/components/community/padel/PadelStandingsBoard';
import BadmintonStandingsBoard from '@/components/community/badminton/BadmintonStandingsBoard';
import PadelMatchCard from '@/components/community/padel/PadelMatchCard';
import { flattenPadelGames } from '@/components/community/padel/padelListUtils';
import { countSetWins } from '@/components/community/badminton/badmintonScoringUtils';
import padelService from '@/services/padelService';
import badmintonService from '@/services/badmintonService';
import BadmintonStatistikPage from '@/pages/community/BadmintonStatistikPage';
import PickleballStatistikPage from '@/pages/community/PickleballStatistikPage';
import { useAuth } from '@/contexts/AuthContext';
import { getClientProfile, DataProfile } from '@/services/clientProfile';
import RaporEventCard from '@/components/client/mobile/RaporEventCard';
import { formatLocalEventTime, formatLocalEventTimeRange } from '@/utils/dateUtils';
import { historyCanGoBack } from '@/utils/nativeNavigation';
import type { PadelSessionListItem, PadelStandingItem } from '@/services/padelService';
import type { BadmintonSessionListItem, BadmintonGameListItem, BadmintonStandingItem } from '@/services/badmintonService';

type ActiveTab = 'match-result' | 'standings' | 'top-score' | 'top-assist' | 'top-mmr' | 'top-save';

type GameStatus = 'SELESAI' | 'LIVE MATCH' | 'UPCOMING';

const formatEventDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getEventStatus = (event: EventDetailResponse): GameStatus => {
  const now = new Date();
  const startTime = event.start_time ? new Date(event.start_time) : null;
  const endTime = event.end_time ? new Date(event.end_time) : null;

  if (endTime && now >= endTime) return 'SELESAI';
  if (startTime && now >= startTime && (!endTime || now < endTime)) return 'LIVE MATCH';
  return 'UPCOMING';
};

const getStatusBadgeClass = (status: GameStatus): string => {
  switch (status) {
    case 'SELESAI':
      return 'bg-[#0E1726] text-white';
    case 'LIVE MATCH':
      return 'bg-red-500 text-white';
    default:
      return 'bg-blue-600 text-white';
  }
};

const getStatusLabel = (status: GameStatus): string => {
  switch (status) {
    case 'SELESAI':
      return 'Selesai';
    case 'LIVE MATCH':
      return 'Live Match';
    default:
      return 'Upcoming';
  }
};

// Helper for team initials
const getTeamInitial = (name?: string): string => {
  if (!name) return 'T';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Helper for team badge color styling matching design improvement
const getTeamBadgeColor = (name?: string): string => {
  const n = (name || '').toLowerCase();
  if (n.includes('green') || n.includes('hijau')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (n.includes('navy') || n.includes('biru tua')) return 'bg-slate-100 text-slate-700 border-slate-300';
  if (n.includes('white') || n.includes('putih')) return 'bg-gray-100 text-gray-700 border-gray-300';
  if (n.includes('orange') || n.includes('oranye')) return 'bg-orange-50 text-orange-600 border-orange-200';
  if (n.includes('red') || n.includes('merah')) return 'bg-red-50 text-red-600 border-red-200';
  if (n.includes('blue') || n.includes('biru')) return 'bg-blue-50 text-blue-600 border-blue-200';
  return 'bg-[#FFF2EE] text-[#FC480D] border-orange-200';
};

const ClientMabarEventHasilPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<DataProfile | null>(null);

  const [event, setEvent] = useState<EventDetailResponse | null>(null);
  const [games, setGames] = useState<GameListItem[]>([]);
  const [standing, setStanding] = useState<StandingItem[]>([]);
  const [padelSessions, setPadelSessions] = useState<PadelSessionListItem[]>([]);
  const [padelStandings, setPadelStandings] = useState<PadelStandingItem[]>([]);
  const [badmintonSessions, setBadmintonSessions] = useState<BadmintonSessionListItem[]>([]);
  const [badmintonStandings, setBadmintonStandings] = useState<BadmintonStandingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ActiveTab | null;
  const validTabs: ActiveTab[] = ['match-result', 'standings', 'top-score', 'top-assist', 'top-mmr', 'top-save'];
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'match-result';
  const [tab, setTab] = useState<ActiveTab>(initialTab);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam) && tabParam !== tab) {
      setTab(tabParam);
    }
  }, [tabParam, tab]);

  const handleTabChange = (newTab: ActiveTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab }, { replace: true });
  };

  const [invasionSession, setInvasionSession] = useState<InvasionSession | null>(null);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);

  useEffect(() => {
    getClientProfile()
      .then((res) => {
        if (res?.success && res.data) {
          setProfile(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const activeDisplayName = profile?.name || user?.name || user?.username || 'Player';
  const activeAvatar = profile?.profile_image || user?.avatar;

  const renderPadelBadminton = () => {
    const isPadel = event?.sport_category?.slug === 'padel';
    const isBadminton = event?.sport_category?.slug === 'badminton';
    const isSparring = !!event?.config?.is_sparring;
    const teamAName = isSparring ? event?.config?.teams?.[0]?.name || 'Tim A' : 'Tim A';
    const teamBName = isSparring ? event?.config?.teams?.[1]?.name || 'Tim B' : 'Tim B';

    if (isBadminton) {
      if (tab === 'standings') {
        return (
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-[inset_0_0_0_1px_#E0E6ED]">
            <BadmintonStandingsBoard
              standings={badmintonStandings}
              eventName={event?.name}
              eventDateFormatted={event?.event_date}
              location={event?.lokasi_arena || event?.city}
              eventSlug={event?.slug}
              userDisplayName={activeDisplayName}
              userAvatar={activeAvatar}
            />
          </div>
        );
      }

      const allBadmintonGames: BadmintonGameListItem[] = [];
      badmintonSessions.forEach((s) => (s.games ?? []).forEach((g) => allBadmintonGames.push(g)));
      if (allBadmintonGames.length === 0) {
        return (
          <div className="bg-white rounded-2xl p-10 text-center shadow-[inset_0_0_0_1px_#E0E6ED]">
            <Icon icon="ph:trophy" className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-[#0E1726]">Belum ada pertandingan badminton</p>
          </div>
        );
      }
      return (
        <div className="space-y-4">
          {allBadmintonGames.map((game, idx) => {
            const { winsA, winsB } = countSetWins(game.setScores ?? []);

            return (
              <PadelMatchCard
                key={game.slug}
                game={{
                  id: game.id ?? 0,
                  slug: game.slug,
                  sessionId: 0,
                  sessionSlug: game.sessionType ?? '',
                  sessionTitle: game.sessionType ?? '',
                  sessionType: game.sessionType ?? '',
                  scoringType: 'sets',
                  status: game.status as any,
                  sideAPlayers: game.sideAPlayers as any,
                  sideBPlayers: game.sideBPlayers as any,
                  scoreSideA: winsA,
                  scoreSideB: winsB,
                  winnerSide: game.winnerSide as any,
                }}
                sessionType={game.sessionType}
                index={idx}
                canEdit={false}
                isSparring={isSparring}
                teamAName={teamAName}
                teamBName={teamBName}
              />
            );
          })}
        </div>
      );
    }

    if (isPadel) {
      if (tab === 'standings') {
        return (
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-[inset_0_0_0_1px_#E0E6ED]">
            <PadelStandingsBoard
              standings={padelStandings}
              isSparring={!!event?.config?.is_sparring}
              teamAName={event?.config?.teams?.[0]?.name || 'Tim A'}
              teamBName={event?.config?.teams?.[1]?.name || 'Tim B'}
              teamAColor={event?.config?.teams?.[0]?.jersey_color}
              teamBColor={event?.config?.teams?.[1]?.jersey_color}
              sessions={padelSessions}
              participants={
                event?.participants as unknown as import('@/services/communityService').MabarParticipant[]
              }
              eventName={event?.name}
              eventDateFormatted={event?.event_date}
              location={event?.lokasi_arena || event?.city}
              eventSlug={event?.slug}
              userDisplayName={activeDisplayName}
              userAvatar={activeAvatar}
            />
          </div>
        );
      }

      const padelGames = flattenPadelGames(padelSessions);
      if (padelGames.length === 0) {
        return (
          <div className="bg-white rounded-2xl p-10 text-center shadow-[inset_0_0_0_1px_#E0E6ED]">
            <Icon icon="ph:trophy" className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-[#0E1726]">Belum ada pertandingan padel</p>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {padelGames.map((item, idx) => {
            const { game, sessionType } = item;
            return (
              <PadelMatchCard
                key={game.slug}
                game={game}
                sessionType={sessionType}
                index={idx}
                canEdit={false}
                isSparring={isSparring}
                teamAName={teamAName}
                teamBName={teamBName}
              />
            );
          })}
        </div>
      );
    }

    return null;
  };

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      const eventData = await mabarService.getEventBySlug(slug);
      if (!eventData) {
        setError('Event tidak ditemukan');
        setLoading(false);
        return;
      }
      const sportSlug = eventData.sport_category?.slug ?? '';
      const sportName = eventData.sport_category?.name ?? '';
      const isSparring = Boolean(eventData.config?.is_sparring);

      if (!hasHasilPage(sportSlug || sportName, isSparring)) {
        navigate(`/event/${slug}`, { replace: true });
        return;
      }
      setEvent(eventData);

      if (sportSlug === 'padel') {
        try {
          const [sessData, standData] = await Promise.all([
            padelService.listSessions(slug),
            padelService.getStandings(slug),
          ]);
          setPadelSessions(sessData ?? []);
          setPadelStandings(standData ?? []);
        } catch {
          setError('Gagal memuat hasil pertandingan padel.');
        }
      } else if (sportSlug === 'badminton') {
        try {
          const [sessData, standData] = await Promise.all([
            badmintonService.listSessions(slug),
            badmintonService.getStandings(slug),
          ]);
          setBadmintonSessions(sessData ?? []);
          setBadmintonStandings(standData ?? []);
        } catch {
          setError('Gagal memuat hasil pertandingan badminton.');
        }
      } else if (sportSlug === 'pickleball') {
        setError(null);
      } else {
        const [gamesData, standingData, sessionData, participantsRes] = await Promise.all([
          mabarService.getGamesByEventSlug(slug),
          mabarService.getStandingsByEventSlug(slug),
          invasionService.getInvasionSession(slug).catch(() => null),
          communityService.getEventParticipants(slug, { limit: 100 }).catch(() => null),
        ]);
        setGames(gamesData ?? []);
        setStanding(standingData ?? []);
        setInvasionSession(sessionData);
        setParticipants(
          participantsRes?.participants ??
            ((eventData.participants as unknown as MabarParticipant[]) || [])
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Gagal memuat data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!loading && error) {
      const t = setTimeout(() => navigate(`/event/${slug || ''}`, { replace: true }), 2000);
      return () => clearTimeout(t);
    }
  }, [loading, error, navigate, slug]);

  // Aggregate stats across games and invasion session
  const playerStats = useMemo<InvasionPlayerLeaderboardItem[]>(() => {
    const map: Record<string, InvasionPlayerLeaderboardItem> = {};

    // 1. Initialize from participants list
    participants.forEach((p) => {
      const key = String(p.id);
      map[key] = {
        id: p.id,
        name: p.name,
        avatar: p.avatar,
        role: p.role,
        team: p.team,
        goals: 0,
        assists: 0,
        saves: 0,
        deltaMmr: 0,
        matchesPlayed: 0,
      };
    });

    const participantIdToKey = (pid?: number | null) => {
      if (!pid) return null;
      return String(pid);
    };

    const userIdToKey = (uid?: number | null) => {
      if (!uid) return null;
      const found = participants.find((p) => p.user_id === uid);
      return found ? String(found.id) : null;
    };

    // 2. Count goals, assists, and saves from timelines of all games
    const timelineGoals: Record<string, number> = {};
    const timelineAssists: Record<string, number> = {};
    const timelineSaves: Record<string, number> = {};

    games.forEach((g) => {
      if (!Array.isArray(g.timeline)) return;
      g.timeline.forEach((item: unknown) => {
        if (!item || typeof item !== 'object') return;
        const e = item as { type?: string; player_id?: number; assist_id?: number };
        if (e.type === 'goal') {
          if (e.player_id) {
            const k = String(e.player_id);
            timelineGoals[k] = (timelineGoals[k] || 0) + 1;
          }
          if (e.assist_id) {
            const ak = String(e.assist_id);
            timelineAssists[ak] = (timelineAssists[ak] || 0) + 1;
          }
        } else if (e.type === 'save') {
          if (e.player_id) {
            const sk = String(e.player_id);
            timelineSaves[sk] = (timelineSaves[sk] || 0) + 1;
          }
        }
      });
    });

    // 3. Process matches & player stats from invasion session
    if (invasionSession?.matches && invasionSession.matches.length > 0) {
      invasionSession.matches.forEach((match) => {
        const isCompleted = match.status === 'completed';
        (match.players || []).forEach((mp) => {
          let key = participantIdToKey(mp.participantId) || userIdToKey(mp.userId);
          if (!key) {
            key = `mp_${mp.id}`;
            map[key] = {
              id: mp.id,
              name: mp.user?.name || mp.user?.nickname || 'Pemain',
              avatar: (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar,
              role: mp.role,
              team: mp.team === 'A' ? match.teamAName : match.teamBName,
              goals: 0,
              assists: 0,
              saves: 0,
              deltaMmr: 0,
              matchesPlayed: 0,
            };
          }

          const p = map[key];
          if (p) {
            if (mp.stats) {
              p.goals += Number(mp.stats.goals) || 0;
              p.assists += Number(mp.stats.assists) || 0;
              p.saves = (p.saves || 0) + (Number((mp.stats as any).saves) || Number((mp.stats as any).save) || 0);
              p.deltaMmr += Number(mp.stats.deltaMmr) || 0;
            }
            if (isCompleted || mp.isOnField) {
              p.matchesPlayed += 1;
            }
            if (!p.role && mp.role) p.role = mp.role;
            if (!p.team && mp.team) {
              p.team = mp.team === 'A' ? match.teamAName : match.teamBName;
            }
            if (!p.avatar && ((mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar)) {
              p.avatar = (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar;
            }
          }
        });
      });
    }

    // 4. Merge timeline goals/assists/saves using Math.max to prevent discrepancies
    Object.keys(timelineGoals).forEach((k) => {
      if (map[k]) {
        map[k].goals = Math.max(map[k].goals, timelineGoals[k]);
        if (map[k].matchesPlayed === 0 && map[k].goals > 0) {
          map[k].matchesPlayed = 1;
        }
      } else {
        map[k] = {
          id: k,
          name: `Pemain #${k}`,
          goals: timelineGoals[k],
          assists: timelineAssists[k] || 0,
          saves: timelineSaves[k] || 0,
          deltaMmr: 0,
          matchesPlayed: 1,
        };
      }
    });

    Object.keys(timelineAssists).forEach((k) => {
      if (map[k]) {
        map[k].assists = Math.max(map[k].assists, timelineAssists[k]);
      }
    });

    Object.keys(timelineSaves).forEach((k) => {
      if (map[k]) {
        map[k].saves = Math.max(map[k].saves || 0, timelineSaves[k]);
        if (map[k].matchesPlayed === 0 && (map[k].saves || 0) > 0) {
          map[k].matchesPlayed = 1;
        }
      } else {
        map[k] = {
          id: k,
          name: `Pemain #${k}`,
          goals: timelineGoals[k] || 0,
          assists: timelineAssists[k] || 0,
          saves: timelineSaves[k],
          deltaMmr: 0,
          matchesPlayed: 1,
        };
      }
    });

    return Object.values(map);
  }, [participants, games, invasionSession]);

  const topScorers = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.goals > 0)
      .sort((a, b) => {
        if (b.goals !== a.goals) return b.goals - a.goals;
        if (b.assists !== a.assists) return b.assists - a.assists;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topAssists = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.assists > 0)
      .sort((a, b) => {
        if (b.assists !== a.assists) return b.assists - a.assists;
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topMmrGainers = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.deltaMmr !== 0 || p.matchesPlayed > 0)
      .sort((a, b) => {
        if (b.deltaMmr !== a.deltaMmr) return b.deltaMmr - a.deltaMmr;
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topSavers = useMemo(() => {
    return [...playerStats]
      .filter((p) => (p.saves ?? 0) > 0)
      .sort((a, b) => {
        if ((b.saves ?? 0) !== (a.saves ?? 0)) return (b.saves ?? 0) - (a.saves ?? 0);
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const sportSlug = (event?.sport_category?.slug ?? '').toLowerCase();
  const isInvasion =
    !['badminton', 'padel', 'pickleball', 'tennis'].includes(sportSlug) ||
    ['mini-soccer', 'minisoccer', 'futsal', 'sepak-bola', 'sepakbola', 'football', 'invasion'].includes(
      sportSlug
    );

  const hostName = event?.host?.name ?? '–';
  const venue = event?.lokasi_arena || event?.address || '–';
  const sportName = event?.sport_category?.name ?? '–';

  if (loading) {
    return (
      <div className=" flex items-center justify-center p-6 py-12">
        <div className="bg-white rounded-2xl p-8 shadow-[inset_0_0_0_1px_#E0E6ED] flex items-center justify-center gap-3">
          <Icon icon="ph:spinner-gap-bold" className="w-6 h-6 text-[#FC480D] animate-spin" />
          <span className="text-sm font-semibold text-[#0E1726]">Memuat data hasil mabar...</span>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className=" flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl p-8 text-center shadow-[inset_0_0_0_1px_#E0E6ED] max-w-sm w-full">
          <Icon icon="ph:warning-circle-bold" className="w-10 h-10 text-red-500 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#0E1726] mb-1">{error || 'Event tidak ditemukan'}</p>
          <p className="text-xs text-[#556575]">Mengalihkan kembali ke Mabar...</p>
        </div>
      </div>
    );
  }

  if (event.sport_category?.slug === 'badminton') {
    return <BadmintonStatistikPage />;
  }

  if (event.sport_category?.slug === 'pickleball') {
    return <PickleballStatistikPage />;
  }

  const status = getEventStatus(event);

  return (
    <div className=" text-[#0E1726] pb-12">
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-4 md:pt-6">
        {/* Desktop Navigation Header */}
        <div className="hidden md:flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => {
              if (historyCanGoBack()) {
                navigate(-1);
              } else {
                navigate('/event');
              }
            }}
            className="w-10 h-10 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-100 transition-colors cursor-pointer shadow-2xs"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#0E1726]">Hasil Mabar</h1>
            <p className="text-xs text-[#556575]">Rekap skor akhir pertandingan dan papan klasemen</p>
          </div>
        </div>

        {/* Event Header Banner Card (Clean White Card Style) */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 border border-[#E0E6ED] mb-6 flex flex-col gap-4 shadow-2xs">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg sm:text-xl font-bold text-[#0E1726] leading-snug">
              {event.name}
            </h2>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${getStatusBadgeClass(status)}`}>
              {getStatusLabel(status)}
            </span>
          </div>

          <div className="flex flex-col gap-3 text-xs">
            {/* Tanggal & Waktu */}
            <div className="flex items-start gap-3">
              <Icon icon="ph:calendar-blank-fill" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[11px] text-[#556575] font-medium">Tanggal & Waktu</span>
                <span className="font-bold text-[#0E1726] text-xs">
                  {formatEventDate(event.event_date)}, {formatLocalEventTimeRange(event.start_time, event.end_time)}
                </span>
              </div>
            </div>

            {/* Host / Komunitas */}
            <div className="flex items-start gap-3">
              <Icon icon="ph:user-fill" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[11px] text-[#556575] font-medium">Host / Komunitas</span>
                <span className="font-bold text-[#0E1726] text-xs">{hostName}</span>
              </div>
            </div>

            {/* Venue */}
            <div className="flex items-start gap-3">
              <Icon icon="ph:map-pin-fill" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[11px] text-[#556575] font-medium">Venue</span>
                <span className="font-bold text-[#0E1726] text-xs">{venue}</span>
              </div>
            </div>

            {/* Jenis Olahraga */}
            <div className="flex items-start gap-3">
              <Icon icon="ph:soccer-ball-fill" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
              <div className="flex flex-col">
                <span className="text-[11px] text-[#556575] font-medium">Jenis Olahraga</span>
                <span className="font-bold text-[#0E1726] text-xs">{sportName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tombol Download Statistik (Invasion Games) */}
        {!['padel', 'badminton', 'pickleball'].includes(event.sport_category?.slug ?? '') && (
          <div className="mb-6">
            <RaporEventCard
              eventSlug={slug}
              userDisplayName={activeDisplayName}
              userAvatar={activeAvatar}
              buttonOnly={true}
            />
          </div>
        )}

        {/* Horizontal Pill Tabs Navigation */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
          <button
            type="button"
            onClick={() => handleTabChange('match-result')}
            className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
              tab === 'match-result'
                ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
            }`}
          >
            <Icon icon="ph:soccer-ball-bold" className={`w-4 h-4 shrink-0 ${tab === 'match-result' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
            <span>Pertandingan</span>
          </button>

          <button
            type="button"
            onClick={() => handleTabChange('standings')}
            className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
              tab === 'standings'
                ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
            }`}
          >
            <Icon icon="ph:list-numbers-bold" className={`w-4 h-4 shrink-0 ${tab === 'standings' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
            <span>Klasemen</span>
          </button>

          {isInvasion && (
            <>
              <button
                type="button"
                onClick={() => handleTabChange('top-score')}
                className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                  tab === 'top-score'
                    ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                    : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
                }`}
              >
                <Icon icon="ph:target-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-score' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                <span>Top Scores</span>
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('top-assist')}
                className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                  tab === 'top-assist'
                    ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                    : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
                }`}
              >
                <Icon icon="ph:handshake-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-assist' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                <span>Top Assist</span>
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('top-mmr')}
                className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                  tab === 'top-mmr'
                    ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                    : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
                }`}
              >
                <Icon icon="ph:trend-up-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-mmr' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                <span>Top MMR</span>
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('top-save')}
                className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                  tab === 'top-save'
                    ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                    : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
                }`}
              >
                <Icon icon="ph:hand-palm-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-save' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
                <span>Top Save</span>
              </button>
            </>
          )}
        </div>

        {/* Content Body */}
        {['padel', 'badminton', 'pickleball'].includes(event.sport_category?.slug ?? '') ? (
          <div>{renderPadelBadminton()}</div>
        ) : tab === 'match-result' ? (
          games.length === 0 ? (
            <div className="bg-white rounded-2xl p-10 text-center border border-[#E0E6ED] shadow-2xs">
              <Icon icon="ph:trophy-bold" className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#0E1726]">Belum ada data pertandingan</p>
            </div>
          ) : (
            <div className="space-y-4">
              {games.map((game, index) => {
                const homeScore = Number(game.home_score) || 0;
                const awayScore = Number(game.away_score) || 0;

                return (
                  <div
                    key={game.id}
                    className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden mb-4"
                  >
                    {/* Game Header Row */}
                    <div className="px-4 py-3 border-b border-[#E0E6ED] flex items-center justify-between bg-white">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xs text-[#0E1726] tracking-wider uppercase">
                          GAME {index + 1}
                        </span>
                        {game.start_time && game.end_time && (
                          <>
                            <span className="text-xs text-gray-300">|</span>
                            <span className="text-xs text-gray-400 font-medium">
                              {formatLocalEventTime(game.start_time, false)} - {formatLocalEventTime(game.end_time, false)}
                            </span>
                          </>
                        )}
                      </div>
                      <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-[11px] font-bold px-2.5 py-0.5 rounded-full uppercase flex items-center gap-1">
                        <Icon icon="ph:check-circle-fill" className="w-3.5 h-3.5 text-emerald-600" />
                        SELESAI
                      </span>
                    </div>

                    {/* Match Score Box */}
                    <div className="p-5 grid grid-cols-3 items-center text-center bg-white">
                      {/* Home Team */}
                      <div className="flex flex-col items-center justify-center min-w-0">
                        <div
                          className={`w-12 h-12 rounded-2xl ${getTeamBadgeColor(
                            game.home_team_name
                          )} border font-bold text-sm flex items-center justify-center shadow-2xs`}
                        >
                          {getTeamInitial(game.home_team_name)}
                        </div>
                        <span className="text-xs font-bold text-[#0E1726] mt-2 truncate w-full text-center">
                          {game.home_team_name || 'Home Team'}
                        </span>
                      </div>

                      {/* Score Center */}
                      <div className="flex flex-col items-center justify-center">
                        <span className="text-2xl sm:text-3xl font-black text-[#0E1726] tracking-tight">
                          {homeScore} - {awayScore}
                        </span>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">
                          {homeScore === awayScore ? 'SERI' : 'FULL TIME'}
                        </span>
                      </div>

                      {/* Away Team */}
                      <div className="flex flex-col items-center justify-center min-w-0">
                        <div
                          className={`w-12 h-12 rounded-2xl ${getTeamBadgeColor(
                            game.away_team_name
                          )} border font-bold text-sm flex items-center justify-center shadow-2xs`}
                        >
                          {getTeamInitial(game.away_team_name)}
                        </div>
                        <span className="text-xs font-bold text-[#0E1726] mt-2 truncate w-full text-center">
                          {game.away_team_name || 'Away Team'}
                        </span>
                      </div>
                    </div>

                    {/* Goal Scorers & Detail Footer Row */}
                    <div className="px-4 py-2.5 bg-[#FAFBFD] border-t border-[#E0E6ED] flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 min-w-0 pr-2">
                        <Icon icon="ph:soccer-ball-fill" className="w-4 h-4 text-[#FC480D] shrink-0" />
                        <span className="text-xs text-[#556575] font-medium truncate">
                          {game.scorers_summary || 'Belum ada data pencetak gol'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/event/${slug}/game/${game.slug}`)}
                        className="text-xs font-bold text-[#FC480D] hover:underline cursor-pointer flex items-center gap-0.5 shrink-0"
                      >
                        <span>Detail</span>
                        <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5 text-[#FC480D]" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : tab === 'standings' ? (
          <StandingCard standings={standing} />
        ) : tab === 'top-score' ? (
          <InvasionPlayerLeaderboard
            type="top-score"
            players={topScorers}
            showActions={false}
          />
        ) : tab === 'top-assist' ? (
          <InvasionPlayerLeaderboard
            type="top-assist"
            players={topAssists}
            showActions={false}
          />
        ) : tab === 'top-mmr' ? (
          <InvasionPlayerLeaderboard
            type="top-mmr"
            players={topMmrGainers}
            showActions={false}
          />
        ) : tab === 'top-save' ? (
          <InvasionPlayerLeaderboard
            type="top-save"
            players={topSavers}
            showActions={false}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ClientMabarEventHasilPage;
